import React, { useState, ReactNode } from 'react';
import { LucideIcon } from 'lucide-react';
import './TabsContainer.css';

export interface Tab {
  id: string;
  label: string;
  icon?: LucideIcon;
  count?: number;
  content: ReactNode;
  actionButton?: ReactNode; // Optional button for this specific tab
}

interface TabsContainerProps {
  breadcrumb?: string;
  title: string;
  subtitle?: string;
  tabs: Tab[];
  defaultTab?: string;
  globalActionButton?: ReactNode; // Optional button that appears for all tabs
  onTabChange?: (tabId: string) => void; // Optional callback when tab changes
}

const TabsContainer: React.FC<TabsContainerProps> = ({
  breadcrumb,
  title,
  subtitle,
  tabs,
  defaultTab,
  globalActionButton,
  onTabChange,
}) => {
  const [activeTab, setActiveTab] = useState<string>(defaultTab || tabs[0]?.id || '');

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    if (onTabChange) {
      onTabChange(tabId);
    }
  };

  // Get the current active tab object
  const currentTab = tabs.find(tab => tab.id === activeTab);

  // Determine which action button to show (tab-specific takes precedence over global)
  const actionButton = currentTab?.actionButton || globalActionButton;

  return (
    <div className="tabs-container">
      {breadcrumb && (
        <div className="tabs-container-breadcrumb">
          {breadcrumb}
        </div>
      )}

      <div className="tabs-container-header">
        <div>
          <h1 className="tabs-container-title">{title}</h1>
          {subtitle && <p className="tabs-container-subtitle">{subtitle}</p>}
        </div>
        {actionButton && (
          <div className="tabs-container-action">
            {actionButton}
          </div>
        )}
      </div>

      <div className="tabs-container-tabs">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              className={`tabs-container-tab ${activeTab === tab.id ? 'tabs-container-tab-active' : ''}`}
              onClick={() => handleTabChange(tab.id)}
            >
              {Icon && <Icon size={16} />}
              {tab.label}
              {tab.count !== undefined && ` (${tab.count})`}
            </button>
          );
        })}
      </div>

      <div className="tabs-container-content">
        {currentTab?.content}
      </div>
    </div>
  );
};

export default TabsContainer;
